import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Building, TrendingUp, Calendar, Save, Mail, Phone, MapPin, Linkedin, Users, Target } from 'lucide-react';
import { Lead } from '@/types/lead';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

interface EditLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead;
  onLeadUpdated: (lead: Lead) => void;
}

const SUB_DISPOSITIONS = [
  'Callback Requested',
  'Not Interested',
  'No Answer',
  'Left Voicemail',
  'Wrong Number',
  'Follow Up Later',
  'Price Too High',
  'Already Enrolled',
  'Needs More Information',
  'Decision Pending',
  'Other',
];

interface UpdateLeadData {
  name: string;
  company: string;
  email?: string;
  phoneNumber?: string;
  industry?: string;
  companySize?: string;
  linkedinUrl?: string;
  location?: string;
  contactPerson?: string;
  priority: 'HOT' | 'WARM' | 'COLD';
  stage: 'NEW' | 'CONTACTED' | 'DISCUSSION' | 'PROPOSAL' | 'NEGOTIATION' | 'WON' | 'LOST';
  dealValue?: number;
  expectedClose?: string;
  sourceId?: string;
  followUpDate?: string;
  subDisposition?: string;
}

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Manufacturing',
  'Retail',
  'Education',
  'Real Estate',
  'Consulting',
  'Marketing',
  'Legal',
  'Fitness',
  'Hospitality',
  'Other'
];

const COMPANY_SIZES = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1000+ employees'
];

const PIPELINE_STAGES = [
  { value: 'NEW', label: 'New', description: 'Initial contact' },
  { value: 'CONTACTED', label: 'Contacted', description: 'First contact made' },
  { value: 'DISCUSSION', label: 'Discussion', description: 'In discussions' },
  { value: 'PROPOSAL', label: 'Proposal', description: 'Proposal sent' },
  { value: 'NEGOTIATION', label: 'Negotiation', description: 'Negotiating terms' },
  { value: 'WON', label: 'Won', description: 'Deal closed' },
  { value: 'LOST', label: 'Lost', description: 'Deal lost' }
];

export const EditLeadModal: React.FC<EditLeadModalProps> = ({
  isOpen,
  onClose,
  lead,
  onLeadUpdated
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<UpdateLeadData>({
    name: '',
    company: '',
    email: '',
    phoneNumber: '',
    industry: '',
    companySize: '',
    linkedinUrl: '',
    location: '',
    contactPerson: '',
    priority: 'WARM',
    stage: 'NEW',
    dealValue: undefined,
    expectedClose: '',
    sourceId: '',
    followUpDate: '',
    subDisposition: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch lead sources for dropdown
  const { data: leadSources } = useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => api.getLeadSources(),
    enabled: isOpen
  });

  // Populate form when lead changes
  useEffect(() => {
    if (lead && isOpen) {
      setFormData({
        name: lead.name || '',
        company: lead.company || '',
        email: lead.email || '',
        phoneNumber: lead.phoneNumber || '',
        industry: lead.industry || '',
        companySize: lead.companySize || '',
        linkedinUrl: lead.linkedinUrl || '',
        location: lead.location || '',
        contactPerson: lead.contactPerson || '',
        priority: (lead.status as 'HOT' | 'WARM' | 'COLD') || 'WARM',
        stage: lead.stage || 'NEW',
        dealValue: lead.value || lead.dealValue || undefined,
        expectedClose: lead.expectedClose ? new Date(lead.expectedClose).toISOString().split('T')[0] : '',
        sourceId: lead.sourceId || lead.source?.id || '',
        followUpDate: (lead as any).followUpDate ? new Date((lead as any).followUpDate).toISOString().split('T')[0] : '',
        subDisposition: (lead as any).subDisposition || ''
      });
      setErrors({});
    }
  }, [lead, isOpen]);

  const updateLeadMutation = useMutation({
    mutationFn: async (data: UpdateLeadData) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/leads/${lead.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: data.name,
            company: data.company,
            email: data.email || null,
            phoneNumber: data.phoneNumber || null,
            industry: data.industry || null,
            companySize: data.companySize || null,
            linkedinUrl: data.linkedinUrl || null,
            location: data.location || null,
            contactPerson: data.contactPerson || null,
            priority: data.priority,
            stage: data.stage,
            value: data.dealValue || 0,
            dealValue: data.dealValue || 0,
            expectedClose: data.expectedClose || null,
            sourceId: data.sourceId || null
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to update lead');
      }

      return result.data || result;
    },
    onSuccess: (updatedLead) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead-detail', lead.id] });
      onLeadUpdated(updatedLead);
      toast({
        title: "Lead updated",
        description: "Lead has been updated successfully.",
      });
      onClose();
    },
    onError: (error: any) => {
      console.error('Failed to update lead:', error);
      const errorMessage = error?.message || 'Failed to update lead. Please try again.';
      setErrors({ submit: errorMessage });
      toast({
        title: "Failed to update lead",
        description: errorMessage,
        variant: "destructive",
      });
    }
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Lead name is required';
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    // Business logic validation
    if (['PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'].includes(formData.stage) && !formData.dealValue) {
      newErrors.dealValue = 'Deal value is required for Proposal stage and beyond';
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // LinkedIn URL validation
    if (formData.linkedinUrl && !formData.linkedinUrl.includes('linkedin.com')) {
      newErrors.linkedinUrl = 'Please enter a valid LinkedIn URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      updateLeadMutation.mutate(formData);
    }
  };

  const handleInputChange = (field: keyof UpdateLeadData, value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const isDealValueVisible = true; // Always show — required for PROPOSAL+ stages
  const isExpectedCloseVisible = ['NEGOTIATION', 'WON', 'LOST'].includes(formData.stage);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <Card className="p-6 bg-white shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                    <Save className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Edit Lead</h2>
                    <p className="text-sm text-gray-600">Update lead information</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Lead Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <User className="h-5 w-5 mr-2 text-blue-600" />
                      Lead Information
                    </h3>

                    {/* Lead Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter lead name"
                        className={`w-full ${errors.name ? 'border-red-500' : ''}`}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="h-4 w-4 inline mr-1 text-gray-500" />
                        Email
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email address"
                        className={`w-full ${errors.email ? 'border-red-500' : ''}`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="h-4 w-4 inline mr-1 text-gray-500" />
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        placeholder="Enter phone number"
                        className="w-full"
                      />
                    </div>

                    {/* LinkedIn */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Linkedin className="h-4 w-4 inline mr-1 text-gray-500" />
                        LinkedIn URL
                      </label>
                      <Input
                        type="url"
                        value={formData.linkedinUrl}
                        onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                        placeholder="https://linkedin.com/in/username"
                        className={`w-full ${errors.linkedinUrl ? 'border-red-500' : ''}`}
                      />
                      {errors.linkedinUrl && <p className="text-red-500 text-xs mt-1">{errors.linkedinUrl}</p>}
                    </div>

                    {/* Contact Person */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Users className="h-4 w-4 inline mr-1 text-gray-500" />
                        Primary Contact
                      </label>
                      <Input
                        type="text"
                        value={formData.contactPerson}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                        placeholder="Enter primary contact person"
                        className="w-full"
                      />
                    </div>

                    {/* Lead Source */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Target className="h-4 w-4 inline mr-1 text-gray-500" />
                        Lead Source
                      </label>
                      <select
                        value={formData.sourceId || ''}
                        onChange={(e) => handleInputChange('sourceId', e.target.value || undefined)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.sourceId ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select lead source</option>
                        {leadSources?.map(source => (
                          <option key={source.id} value={source.id}>
                            {source.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Company Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Building className="h-5 w-5 mr-2 text-green-600" />
                      Company Information
                    </h3>

                    {/* Company Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        placeholder="Enter company name"
                        className={`w-full ${errors.company ? 'border-red-500' : ''}`}
                      />
                      {errors.company && <p className="text-red-500 text-xs mt-1">{errors.company}</p>}
                    </div>

                    {/* Industry */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => handleInputChange('industry', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select industry</option>
                        {INDUSTRIES.map(industry => (
                          <option key={industry} value={industry}>{industry}</option>
                        ))}
                      </select>
                    </div>

                    {/* Company Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size
                      </label>
                      <select
                        value={formData.companySize}
                        onChange={(e) => handleInputChange('companySize', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select company size</option>
                        {COMPANY_SIZES.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>

                    {/* Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="h-4 w-4 inline mr-1 text-gray-500" />
                        Location
                      </label>
                      <Input
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        placeholder="Enter location"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Deal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Target className="h-5 w-5 mr-2 text-purple-600" />
                    Deal Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority Status
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value as 'HOT' | 'WARM' | 'COLD')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="HOT">Hot</option>
                        <option value="WARM">Warm</option>
                        <option value="COLD">Cold</option>
                      </select>
                    </div>

                    {/* Pipeline Stage */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pipeline Stage
                      </label>
                      <select
                        value={formData.stage}
                        onChange={(e) => handleInputChange('stage', e.target.value as Lead['stage'])}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {PIPELINE_STAGES.map(stage => (
                          <option key={stage.value} value={stage.value}>
                            {stage.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Deal Value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <TrendingUp className="h-4 w-4 inline mr-1 text-gray-500" />
                      Deal Value (₹) {['PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'].includes(formData.stage) ? '*' : ''}
                    </label>
                    <Input
                      type="number"
                      value={formData.dealValue || ''}
                      onChange={(e) => handleInputChange('dealValue', parseInt(e.target.value) || undefined)}
                      placeholder="Enter deal value"
                      className={`w-full ${errors.dealValue ? 'border-red-500' : ''}`}
                      min="0"
                    />
                    {errors.dealValue && <p className="text-red-500 text-xs mt-1">{errors.dealValue}</p>}
                  </div>

                  {/* Expected Close Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="h-4 w-4 inline mr-1 text-gray-500" />
                      Expected Close Date
                    </label>
                    <Input
                      type="date"
                      value={formData.expectedClose}
                      onChange={(e) => handleInputChange('expectedClose', e.target.value)}
                      className={`w-full ${errors.expectedClose ? 'border-red-500' : ''}`}
                    />
                    {errors.expectedClose && <p className="text-red-500 text-xs mt-1">{errors.expectedClose}</p>}
                  </div>
                </div>

                {/* Follow-up & Disposition */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-orange-500" />
                    Follow-up Tracking
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Follow-up Date
                      </label>
                      <Input
                        type="date"
                        value={formData.followUpDate || ''}
                        onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sub-Disposition
                      </label>
                      <select
                        value={formData.subDisposition || ''}
                        onChange={(e) => handleInputChange('subDisposition', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select disposition</option>
                        {SUB_DISPOSITIONS.map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-600 text-sm">{errors.submit}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateLeadMutation.isPending}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    {updateLeadMutation.isPending ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </div>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
